// app.email — send email from the project's domain + own the project's
// Cognito lifecycle templates.
//
// Thin client façade over the inbox/outputs RPC: `app.email.send(...)` enqueues
// the built-in `send-email` server function (auto-bundled into every project's
// runtime), which performs the actual SESv2 send off-browser with abuse guards
// (auth, From-pinning, quota, audit log). The browser never holds SES rights —
// it only enqueues a task, exactly like `app.fn("send-email")(input)`.
//
// Lifecycle TEMPLATES (sign-up verify / invite / forgot-password) are a
// per-project override at `paths().emailTemplate(kind)` — the SAME key the
// runtime's CustomMessage trigger reads. `getTemplate`/`setTemplate`/
// `resetTemplate` are the programmatic form of the console's Email panel, so
// any app can restyle its own lifecycle mail. IAM-gated (`.s3p/` writes need
// an admin-tier role).
//
// Structured as its own module so the email surface can later be lifted into a
// standalone, sellable package without touching the rest of the SDK.

import {
  DeleteObjectCommand,
  GetObjectCommand,
  PutObjectCommand,
  type S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";
import type { SendEmailInput } from "./functions.ts";
import type { CallsModule } from "./calls.ts";
import type { Paths } from "./paths.ts";
import {
  type CognitoMessageKind,
  DEFAULT_EMAIL_TEMPLATES,
  type EmailTemplate,
  assertTemplateUsable,
} from "./email-templates.ts";

export type { SendEmailInput } from "./functions.ts";
export type { CognitoMessageKind, EmailTemplate } from "./email-templates.ts";

export interface TemplateReadResult {
  template: EmailTemplate;
  /** Whether the project has its own override or is riding the s3p default. */
  source: "override" | "default";
}

export interface EmailModule {
  /** Enqueue an email send. Resolves with the SES message id once the runtime
   *  completes the task; rejects with the handler error (quota, bad From, …). */
  send(input: SendEmailInput): Promise<{ messageId: string }>;
  /** The lifecycle template the runtime will actually render for `kind` —
   *  the project's override if one exists, else the s3p default. */
  getTemplate(kind: CognitoMessageKind): Promise<TemplateReadResult>;
  /** Write the project's override for `kind`. Templates whose default carries
   *  the `{####}` code placeholder must keep it — the runtime falls back to
   *  the default otherwise, which makes the edit look ignored. We reject the
   *  write instead of letting that happen silently. */
  setTemplate(kind: CognitoMessageKind, template: EmailTemplate): Promise<void>;
  /** Drop the override — the runtime renders the s3p default again. */
  resetTemplate(kind: CognitoMessageKind): Promise<void>;
}

export interface EmailConfig {
  calls: CallsModule;
  getClient: () => Promise<S3Client>;
  bucket: string;
  /** Usually "" — kept so template keys build exactly like every other module. */
  bucketPrefix: string;
  paths: Paths;
}

export function createEmail(cfg: EmailConfig): EmailModule {
  const keyFor = (kind: CognitoMessageKind) => `${cfg.bucketPrefix}${cfg.paths.emailTemplate(kind)}`;

  function defaultFor(kind: CognitoMessageKind): EmailTemplate {
    const t = DEFAULT_EMAIL_TEMPLATES[kind];
    if (!t) throw new Error(`app.email: unknown template kind '${kind}'`);
    return t;
  }

  return {
    async send(input: SendEmailInput): Promise<{ messageId: string }> {
      return (await cfg.calls.invoke("send-email", input)) as { messageId: string };
    },

    async getTemplate(kind) {
      const fallback = defaultFor(kind);
      try {
        const s3 = await cfg.getClient();
        const r = await s3.send(new GetObjectCommand({ Bucket: cfg.bucket, Key: keyFor(kind) }));
        const text = await (
          r.Body as { transformToString: () => Promise<string> }
        ).transformToString();
        const parsed = JSON.parse(text) as Partial<EmailTemplate>;
        if (parsed.subject && (parsed.html || parsed.text)) {
          return {
            template: {
              subject: parsed.subject,
              html: parsed.html ?? "",
              text: parsed.text ?? "",
            },
            source: "override",
          };
        }
      } catch {
        // missing / unreadable → the runtime uses the default; so do we
      }
      return { template: fallback, source: "default" };
    },

    async setTemplate(kind, template) {
      assertTemplateUsable(kind, template);
      const s3 = await cfg.getClient();
      await s3.send(
        new PutObjectCommand({
          Bucket: cfg.bucket,
          Key: keyFor(kind),
          Body: JSON.stringify(template, null, 2),
          ContentType: "application/json",
        }),
      );
    },

    async resetTemplate(kind) {
      defaultFor(kind); // validate the kind before touching the bucket
      const s3 = await cfg.getClient();
      await s3.send(new DeleteObjectCommand({ Bucket: cfg.bucket, Key: keyFor(kind) }));
    },
  };
}
